import { useEffect, useRef } from "react";

/**
 * Animated "plexus" network background.
 * Bright NVIDIA-green nodes connected by glowing lines that fade with distance,
 * drifting slowly. Rendered on a full-screen fixed canvas behind page content.
 */
const NetworkBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const wrapper = canvas.parentElement;

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let nodes = [];
    let raf = 0;
    let running = false;
    let videoPlaying = false;
    const mouse = { x: -9999, y: -9999 };

    const GREEN = "142, 229, 0"; // bright lime (#8ee500)
    const GREEN_DIM = "118, 185, 0"; // #76b900
    let LINK_DIST = 190;

    const setPerformanceState = (state) => {
      canvas.dataset.carthyPerformance = "network-background";
      canvas.dataset.carthyPerformanceState = state;
      if (wrapper) {
        wrapper.dataset.carthyPerformance = "network-background";
        wrapper.dataset.carthyPerformanceState = state;
      }
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      LINK_DIST = width < 768 ? 130 : 190;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const density = Math.min(140, Math.floor((width * height) / 11000));
      nodes = new Array(density).fill(0).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2.6 + 1.6,
      }));
    };

    const step = () => {
      if (!running) return;

      ctx.clearRect(0, 0, width, height);

      // draw links first
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;

        for (let j = i + 1; j < nodes.length; j++) {
          const m = nodes[j];
          const dx = n.x - m.x;
          const dy = n.y - m.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DIST) {
            const alpha = (1 - dist / LINK_DIST) * 0.55;
            ctx.strokeStyle = `rgba(${GREEN_DIM}, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(n.x, n.y);
            ctx.lineTo(m.x, m.y);
            ctx.stroke();
          }
        }

        // link to cursor
        const mdx = n.x - mouse.x;
        const mdy = n.y - mouse.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 260) {
          const alpha = (1 - mdist / 260) * 0.8;
          ctx.strokeStyle = `rgba(${GREEN}, ${alpha})`;
          ctx.lineWidth = 1.1;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }
      }

      // draw glowing nodes on top
      for (const n of nodes) {
        ctx.beginPath();
        ctx.fillStyle = `rgba(${GREEN}, 0.95)`;
        ctx.shadowColor = `rgba(${GREEN}, 1)`;
        ctx.shadowBlur = 14;
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      raf = requestAnimationFrame(step);
    };

    const startAnimation = () => {
      if (running || document.hidden || videoPlaying) return;
      running = true;
      setPerformanceState("active");
      raf = requestAnimationFrame(step);
    };

    const stopAnimation = (reason = "paused") => {
      if (running) {
        running = false;
        cancelAnimationFrame(raf);
      }
      setPerformanceState(reason);
    };

    let cursorActive = true;
    let hoverCount = 0; // supports multiple cards being briefly hovered

    const onMove = (e) => {
      if (!cursorActive || !running) return;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    const onTiltHover = (e) => {
      if (e.detail && e.detail.active) {
        hoverCount++;
        cursorActive = false;
        mouse.x = -9999;
        mouse.y = -9999;
      } else {
        hoverCount = Math.max(0, hoverCount - 1);
        if (hoverCount === 0) cursorActive = true;
      }
    };

    const onVideoPerformance = (event) => {
      videoPlaying = Boolean(event.detail?.playing);
      if (videoPlaying) stopAnimation("paused-video");
      else startAnimation();
    };

    const onVisibilityChange = () => {
      if (document.hidden) stopAnimation("paused-hidden");
      else if (!videoPlaying) startAnimation();
    };

    resize();
    setPerformanceState("ready");
    startAnimation();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);
    window.addEventListener("tilt:hover", onTiltHover);
    window.addEventListener("carthy-video-performance", onVideoPerformance);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("tilt:hover", onTiltHover);
      window.removeEventListener("carthy-video-performance", onVideoPerformance);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <div
      data-testid="network-background"
      data-carthy-performance="network-background"
      data-carthy-performance-state="initializing"
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 bg-black network-background transition-opacity duration-300"
    >
      {/* soft ambient green glows in corners (like the reference), no dark veil over center */}
      <div className="absolute -bottom-40 -right-40 h-[70vh] w-[70vh] rounded-full bg-[radial-gradient(circle,_rgba(118,185,0,0.12)_0%,_transparent_70%)]" />
      <div className="absolute -top-40 -left-40 h-[50vh] w-[50vh] rounded-full bg-[radial-gradient(circle,_rgba(118,185,0,0.08)_0%,_transparent_70%)]" />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
};

export default NetworkBackground;
